import React, { useState } from 'react';
import { User } from 'lucide-react';
import { Input } from './ui/input';
import { FormButton } from './ui/form-button';
import { FormField } from './ui/form_field';
import { useToast } from './ui/toast';
import { getFormIconClassName } from '@/lib/utils';
import { router } from '@inertiajs/react';

interface ProfileCompletionModalProps {
  isOpen: boolean;
}


export default function ProfileCompletionModal({ isOpen }: ProfileCompletionModalProps) {
  const { showToast } = useToast();
  const [name, setName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string[]>>({});

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrors({});

    try {
      const response = await window.fetch('/users/profile', {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
        },
        body: JSON.stringify({ name })
      });

      const data = await response.json();

      if (data.success) {
        showToast('success', 'Profile updated successfully!');
        // Reload all page data to ensure member list shows updated name
        router.reload();
      } else {
        setErrors(data.errors || {});
        showToast('error', 'Please fix the errors below');
      }
    } catch (error) {
      console.error('Profile update error:', error);
      showToast('error', 'Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="w-full max-w-md mx-4">
        <div className="bg-white dark:bg-zinc-900 rounded-2xl shadow-xl border">
          <div className="p-6">
            <h2 className="text-xl font-semibold mb-2">Welcome to Infralyst!</h2>
            <p className="text-surface-foreground/70 text-sm mb-6">
              Add your name. This helps your teammates recognize you.
            </p>

            <form onSubmit={handleSubmit} className="space-y-4">
              <FormField
                id="name"
                label="Your name"
                error={errors.name?.[0]}
              >
                <Input
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. Alex Smith"
                  aria-invalid={!!errors.name}
                  leftIcon={
                    <User
                      className={getFormIconClassName(!!errors.name)}
                    />
                  }
                />
              </FormField>

              <FormButton
                type="submit"
                isLoading={isSubmitting}
                loadingText="Saving..."
                className="w-full"
              >
                Save
              </FormButton>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}